import { fromByteArray, toByteArray } from "base64-js";
import { AES_CBC, TRACK_EVENTS } from "core/consts";
import { CryptoWorker, cryptoService } from "core/model/crypto/singleton";
import { differenceInMilliseconds } from "date-fns";
import { ToType, TrackEventFn } from "../../types";

/*
 * For Web Crypto examples see: https://github.com/diafygi/webcrypto-examples
 */

type DataToEncrypt =
  | ArrayBuffer
  | DataView
  | Float32Array
  | Float64Array
  | Int8Array
  | Int16Array
  | Int32Array
  | Uint8Array
  | Uint8ClampedArray
  | Uint16Array
  | Uint32Array;

export function hasCrypto(): boolean {
  return !!cryptoService?.subtle;
}

export function bytesToBase64(bytes: ToType | Uint8Array) {
  const isUint8Array = bytes instanceof Uint8Array;
  return fromByteArray(isUint8Array ? bytes : new Uint8Array(bytes));
}

export function base64ToBytes(string: any) {
  return toByteArray(string);
}

export function generateSalt() {
  return cryptoService.getRandomValues(new Uint8Array(16));
}

export async function generateSaltedHash(data: string, salt: string) {
  if (!hasCrypto()) return undefined;

  const salted = data.concat(salt);

  try {
    const buffer = await cryptoService.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(salted),
    );

    // convert bytes to hex string
    const readableBuffer = Array.from(new Uint8Array(buffer))
      .map((b) => ("00" + b.toString(16)).slice(-2))
      .join("");

    return readableBuffer;
  } catch (e) {
    console.error("Could not hash password", e);
    return undefined;
  }
}

export function generateAESKey(algo: string) {
  return cryptoService.subtle.generateKey(
    {
      length: 256,
      name: algo,
    },
    true,
    ["encrypt", "decrypt"],
  );
}

function deriveAESKeyFallback(
  password: BufferSource,
  salt: Uint8Array,
  iterations: number,
): Promise<string | BufferSource> {
  return new Promise((resolve, reject) => {
    // @ts-ignore
    const worker = new CryptoWorker();
    worker.onmessage = ({ data }: ToType) => {
      const { payload, type } = data;
      switch (type) {
        case "keyDerived": {
          const { derivedKey } = payload;
          resolve(derivedKey);
          return;
        }
        default:
          reject(new Error(`unhandled worker message type: ${type}`));
      }
      worker.terminate();
    };
    worker.postMessage({
      payload: {
        iterations,
        len: 16,
        password,
        salt,
      },
      type: "deriveKey",
    });
  });
}

export function importAESKey(
  key: BufferSource | string,
  algorithm: string | null,
) {
  return cryptoService.subtle.importKey(
    "raw",
    typeof key === "string" ? toByteArray(key) : (key as any),
    { name: algorithm || AES_CBC },
    true,
    ["encrypt", "decrypt"],
  );
}

async function deriveAESKeyOperation(
  password: BufferSource,
  salt: Uint8Array,
  iterations: number,
) {
  let key;

  try {
    const cryptoKey = await cryptoService.subtle.importKey(
      "raw",
      password,
      { name: "PBKDF2" },
      false,
      ["deriveKey"],
    );

    key = cryptoService.subtle.deriveKey(
      {
        hash: "SHA-256",
        iterations,
        name: "PBKDF2",
        salt: salt as any,
      },
      cryptoKey,
      {
        length: 128,
        name: "AES-CBC",
      },
      true,
      ["encrypt", "decrypt"],
    );
  } catch (_e) {
    key = importAESKey(
      await deriveAESKeyFallback(password, salt, iterations),
      null,
    );
  }

  return key;
}

export async function deriveAESKey({
  iterations,
  password,
  salt,
  trackEvent,
}: {
  iterations: number;
  password: DataToEncrypt;
  salt: Uint8Array;
  trackEvent: TrackEventFn;
}) {
  const startTime = new Date();
  const key = await deriveAESKeyOperation(password as any, salt, iterations);
  const duration = differenceInMilliseconds(new Date(), startTime);

  trackEvent({
    duration,
    iterations,
    name: TRACK_EVENTS.DERIVE_AES_KEY,
  });
  return key;
}

export async function generateRSAKeysJWK() {
  const { privateKey, publicKey } = await cryptoService.subtle.generateKey(
    {
      hash: { name: "SHA-256" },
      modulusLength: 2048,
      name: "RSA-OAEP",
      publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
    },
    true,
    ["encrypt", "decrypt"],
  );

  return {
    privateKey: await cryptoService.subtle.exportKey(
      "jwk",
      privateKey,
    ),
    publicKey: await cryptoService.subtle.exportKey(
      "jwk",
      publicKey,
    ),
  };
}

export function importPublicKey(key: CryptoKey | string) {
  if (typeof key !== "string")
    throw new Error(`key must be a string: ${JSON.stringify(key)}`);
  return cryptoService.subtle.importKey(
    "jwk",
    JSON.parse(key),
    {
      hash: { name: "SHA-256" },
      name: "RSA-OAEP",
    },
    false,
    ["encrypt"],
  );
}

export async function importPrivateKey(key: CryptoKey | string | undefined) {
  if (typeof key !== "string") {
    console.error("key must be a string", JSON.stringify(key));
    return null;
  }

  try {
    return await cryptoService.subtle.importKey(
      "jwk",
      JSON.parse(key),
      {
        hash: { name: "SHA-256" },
        name: "RSA-OAEP",
      },
      false,
      ["decrypt"],
    );
  } catch (e) {
    console.error("Could not import private key", key, e);
    return null;
  }
}

export function RSADecrypt(data: DataToEncrypt, key: CryptoKey) {
  return cryptoService.subtle.decrypt(
    {
      hash: { name: "SHA-256" },
      name: "RSA-OAEP",
    } as RsaOaepParams,
    key,
    data as any,
  );
}

export function RSAEncrypt(data: BufferSource, key: CryptoKey) {
  return cryptoService.subtle.encrypt(
    {
      hash: { name: "SHA-256" },
      name: "RSA-OAEP",
    } as RsaOaepParams,
    key,
    data,
  );
}

export function AESDecrypt(
  data: DataToEncrypt,
  key: CryptoKey,
  iv: Uint8Array,
) {
  return cryptoService.subtle.decrypt(
    {
      iv: iv as any,
      name: "AES-CBC",
    },
    key,
    data as any,
  );
}

export function AESEncrypt(
  data: DataToEncrypt,
  key: CryptoKey,
  iv: Uint8Array,
) {
  return cryptoService.subtle.encrypt(
    {
      iv: iv as any,
      name: "AES-CBC",
    },
    key,
    data as any,
  );
}

export function exportKey(key: CryptoKey) {
  return cryptoService.subtle.exportKey("raw", key);
}
